/**
 * Column model for the Performance Report.
 *
 * The API sends `metrics: [{ key, label, short, unit, summable, variants, variances }]`.
 * Everything here derives from that list, so a metric gaining a Budget column or a
 * new variance appears without touching this file.
 *
 * `variances` matters: a metric only gets the delta columns the API says it has.
 * Consignments, for example, expose an absolute PY difference but no percentage —
 * percentages come from dbt, and the UI never invents one.
 */

/** Column blocks the user can toggle. Actual is always on — hiding it would leave
 *  variances with nothing to compare against. */
export const COLUMN_GROUPS = [
  { key: "actual", label: "Actual", locked: true },
  { key: "budget", label: "Budget", locked: false },
  { key: "py", label: "Prior Year", locked: false },
];

/** Appended to a metric's short name. Empty where the short name already carries
 *  the unit ("GP %", "#Cons Inv") — "CW GP % (%)" reads as a typo. */
const UNIT_SUFFIX = {
  currency: "($)",
  ratio: "",
  count: "",
};

/* ── Formatters ─────────────────────────────────────────────────────────── */

/**
 * Money at full precision, always two decimals.
 *
 * Deliberately NOT abbreviated to $1.23M: these figures get read against Finance's
 * own numbers, and a rounded headline cannot be reconciled against a cent. The cost
 * is column width, which the horizontal scroll and the pinned identity columns absorb.
 */
function formatCurrency(value) {
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Counts stay whole — a consignment is never a fraction. */
function formatCount(value) {
  return Math.round(value).toLocaleString();
}

/**
 * A null is never a zero. A consignment can be despatched but not yet invoiced,
 * which is a real count with a genuinely absent revenue; rendering that as $0.00
 * would misstate the week.
 */
export function formatValue(value, unit) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (unit === "currency") return formatCurrency(n);
  if (unit === "ratio") return `${n.toFixed(2)}%`;
  return formatCount(n);
}

/** Absolute variance, always signed so direction reads without relying on colour. */
export function formatDelta(value, unit) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  const abs = Math.abs(n);
  // A margin gap is in percentage points; "pp" stops it being read as a percentage
  // change of the margin.
  if (unit === "ratio") return `${sign}${abs.toFixed(2)}pp`;
  if (unit === "currency") return `${sign}${formatCurrency(abs)}`;
  return `${sign}${formatCount(abs)}`;
}

export function formatDeltaPct(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${Math.abs(n).toFixed(2)}%`;
}

/**
 * Tooltip form. Cells already show full precision, so this adds only the currency
 * code — useful when a figure is copied out of context.
 */
export function formatExact(value, unit) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  if (unit === "currency") {
    return n.toLocaleString(undefined, {
      style: "currency",
      currency: "AUD",
      currencyDisplay: "code",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  if (unit === "ratio") return `${n.toFixed(2)}%`;
  return n.toLocaleString();
}

/* ── Column construction ────────────────────────────────────────────────── */

/** Which group a variance belongs to, so the Budget/PY toggles hide it with its value. */
const VARIANCE_GROUP = {
  var_bud: "budget",
  var_bud_pct: "budget",
  var_py: "py",
  var_py_pct: "py",
};

const VARIANCE_LABEL = {
  var_bud: "vs BUD",
  var_bud_pct: "vs BUD %",
  var_py: "vs PY",
  var_py_pct: "vs PY %",
};

/** Emission order within a metric: value, then its deltas. */
const GROUP_SEQUENCE = [
  { variant: "actual", variances: [] },
  { variant: "budget", variances: ["var_bud", "var_bud_pct"] },
  { variant: "py", variances: ["var_py", "var_py_pct"] },
];

/**
 * Flatten the metric registry into ordered column definitions.
 *
 * @param metrics       registry from the API
 * @param periodShort   tab prefix, e.g. "CW" — mirrors the source report's naming
 * @param visibleGroups Set of enabled group keys
 */
export function buildColumns(metrics, periodShort, visibleGroups) {
  const columns = [];
  const prefix = periodShort ? `${periodShort} ` : "";

  (metrics ?? []).forEach((metric) => {
    const { key, short, unit, variants, variances, summable } = metric;
    const suffix = UNIT_SUFFIX[unit] ?? "";
    const available = new Set(variances ?? []);

    const push = (column) =>
      columns.push({ metricKey: key, unit, summable: summable !== false, ...column });

    GROUP_SEQUENCE.forEach(({ variant, variances: deltas }) => {
      const group = variant === "actual" ? "actual" : variant;
      if (!variants?.includes(variant) || !visibleGroups.has(group)) return;

      const label =
        variant === "actual"
          ? `${prefix}${short} ${suffix}`.trim()
          : `${prefix}${variant === "budget" ? "BUD" : "PY"} ${short}`.trim();

      push({
        id: `${key}.${variant}`,
        field: variant,
        group,
        label,
        kind: "value",
        // Budget and prior year are reference points, not the headline figure.
        tone: variant === "actual" ? undefined : "reference",
      });

      // Only the deltas the API actually supplies for this metric.
      deltas.filter((name) => available.has(name)).forEach((name) => {
        push({
          id: `${key}.${name}`,
          field: name,
          group: VARIANCE_GROUP[name],
          label: VARIANCE_LABEL[name],
          kind: name.endsWith("_pct") ? "deltaPct" : "delta",
        });
      });
    });
  });

  return columns;
}

/** Group columns by metric so the header can render a spanning top row. */
export function buildHeaderGroups(columns, metrics) {
  const byMetric = new Map();
  columns.forEach((column) => {
    if (!byMetric.has(column.metricKey)) byMetric.set(column.metricKey, []);
    byMetric.get(column.metricKey).push(column);
  });

  return [...byMetric.entries()].map(([metricKey, metricColumns]) => ({
    metricKey,
    label: (metrics ?? []).find((m) => m.key === metricKey)?.label ?? metricKey,
    span: metricColumns.length,
  }));
}

/* ── Cell access ────────────────────────────────────────────────────────── */

export function readCell(row, column) {
  const value = row?.metrics?.[column.metricKey]?.[column.field];
  return value === undefined ? null : value;
}

/**
 * Totals only exist for exact sums. Margins and percentage variances are computed
 * in dbt from period totals, so the API omits them from the totals row rather than
 * re-deriving them across an arbitrary set of rows — a figure that would not match
 * Finance's checks. Those cells render blank.
 */
export function totalIsAvailable(totals, column) {
  const metric = totals?.[column.metricKey];
  return !!metric && Object.prototype.hasOwnProperty.call(metric, column.field);
}

export function sortTree(nodes, column, direction) {
  const factor = direction === "asc" ? 1 : -1;

  const compare = (a, b) => {
    if (!column) return factor * String(a.name).localeCompare(String(b.name));
    const av = readCell(a, column);
    const bv = readCell(b, column);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return factor * (Number(av) - Number(bv));
  };

  return [...nodes].sort(compare).map((node) => ({
    ...node,
    children: node.children?.length ? sortTree(node.children, column, direction) : [],
  }));
}

export function filterTree(nodes, term) {
  if (!term) return nodes;
  const needle = term.trim().toLowerCase();
  if (!needle) return nodes;

  const walk = (list) =>
    list.reduce((kept, node) => {
      const children = walk(node.children ?? []);
      const selfMatch = [node.name, node.billing_code].some((field) =>
        String(field ?? "").toLowerCase().includes(needle),
      );
      if (selfMatch || children.length) {
        kept.push({ ...node, children });
      }
      return kept;
    }, []);

  return walk(nodes);
}

export function toCsv(rows, columns) {
  const header = [
    "Billing Company",
    "Billing Code",
    "Parent Company",
    "Child Company",
    ...columns.map((c) => c.label),
  ];

  const lines = [header];
  rows.forEach((row) => {
    lines.push([
      row.billing_company ?? row.name ?? "",
      row.billing_code ?? "",
      row.parent_company ?? "",
      row.child_company ?? "",
      ...columns.map((column) => {
        const cell = readCell(row, column);
        return cell === null || cell === undefined ? "" : cell;
      }),
    ]);
  });

  return lines
    .map((line) =>
      line
        .map((cell) => {
          const text = String(cell ?? "");
          return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        })
        .join(","),
    )
    .join("\n");
}

export function downloadCsv(csv, filename) {
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
